import { Link } from 'react-router-dom';
import { MapPin, Heart, EyeOff, Tv, Wine, Sun, Dog } from 'lucide-react';
import { Card, CardContent, Badge } from '@/components/ui';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useFilters } from '@/contexts/FilterContext';
import type { Bar } from '@/api/endpoints/bars';

interface BarCardProps {
  bar: Bar;
}

export function BarCard({ bar }: BarCardProps) {
  const { isFavorite, toggleFavorite, toggleHidden } = useUserPreferences();
  const { locationSource } = useFilters();
  const barIsFavorite = isFavorite(bar.id);

  // Only show distance when using GPS (not when viewing a city page)
  const distanceText =
    locationSource === 'gps' && bar.distance
      ? bar.distance < 1000
        ? `${Math.round(bar.distance)}m`
        : `${(bar.distance / 1609.34).toFixed(1)} mi`
      : null;

  // Use slug for SEO-friendly URLs, fall back to ID
  const barPath = bar.slug || bar.id;

  return (
    <Card
      lift
      className={`overflow-hidden ${barIsFavorite ? 'card-favorite' : ''}`}
    >
      <Link to={`/bars/${barPath}`}>
        <CardContent className="relative p-3 overflow-hidden">
          {/* Background logo - faded, right-aligned */}
          {bar.logoUrl && (
            <img
              src={bar.logoUrl}
              alt=""
              aria-hidden="true"
              className="card-logo-bg"
            />
          )}
          {/* Content */}
          <div className="relative z-10">
            {/* Name */}
            <h3 className="truncate text-base font-semibold">{bar.name}</h3>

            {/* Address + Distance */}
            {bar.address && (
              <p className="mt-0.5 flex items-center gap-1 text-sm text-muted">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{bar.address.split(',')[0]}</span>
                {distanceText && (
                  <span className="shrink-0">· {distanceText}</span>
                )}
              </p>
            )}

            {/* Description */}
            {bar.description && (
              <p className="mt-1 line-clamp-2 text-sm text-muted">
                {bar.description}
              </p>
            )}

            {/* Badges + Action buttons */}
            <div className="mt-2 flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-1.5">
                {bar.goodForWatchingSports && (
                  <Badge variant="secondary"><Tv className="h-3 w-3" /> Sports</Badge>
                )}
                {bar.servesBrunch && <Badge variant="primary"><Wine className="h-3 w-3" /> Brunch</Badge>}
                {bar.outdoorSeating && <Badge><Sun className="h-3 w-3" /> Patio</Badge>}
                {bar.allowsDogs && <Badge><Dog className="h-3 w-3" /> Dog Friendly</Badge>}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    toggleHidden(bar.id);
                  }}
                  className="rounded-lg p-2 transition-colors hover:bg-border"
                  aria-label="Hide this bar"
                >
                  <EyeOff className="h-5 w-5 text-muted" />
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    toggleFavorite(bar.id);
                  }}
                  className="-mr-2 rounded-lg p-2 transition-colors hover:bg-border"
                  aria-label={
                    barIsFavorite ? 'Remove from favorites' : 'Add to favorites'
                  }
                >
                  <Heart
                    className={`h-5 w-5 ${barIsFavorite ? 'fill-accent-primary text-accent-primary' : 'text-muted'}`}
                  />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
